import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Database, GitBranch } from "lucide-react"

export function MongoDBOverviewPage() {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Database className="h-10 w-10 text-primary" />
          <h1 className="text-5xl font-bold">NestJS MongoDB Template</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed">
          Production-ready NestJS template with MongoDB, Mongoose ODM, JWT authentication, 
          OAuth integration, and role-based access control. Perfect for rapid development 
          with flexible data modeling.
        </p>
        <div className="flex gap-3 pt-2">
          <Badge variant="secondary" className="text-base py-1.5 px-3">NestJS 11</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">MongoDB</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">Mongoose</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">JWT Auth</Badge>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLinkCard
          title="Quick Start"
          description="Get the template running in minutes with our step-by-step guide"
          href="/nestjs/mongodb/quick-start"
        />
        <QuickLinkCard
          title="Features"
          description="Explore all the built-in features and capabilities"
          href="/nestjs/mongodb/features"
        />
        <QuickLinkCard
          title="Project Structure"
          description="Understand the codebase organization and file structure"
          href="/nestjs/mongodb/structure"
        />
        <QuickLinkCard
          title="Configuration"
          description="Learn how to configure the application and environment"
          href="/nestjs/mongodb/configuration"
        />
        <QuickLinkCard
          title="Authentication"
          description="Deep dive into the authentication system and OAuth"
          href="/nestjs/mongodb/authentication"
        />
        <QuickLinkCard
          title="Available Scripts"
          description="npm commands for development and production"
          href="/nestjs/mongodb/scripts"
        />
      </div>

      {/* What's Included */}
      <div className="bg-muted/30 rounded-xl p-8 space-y-6">
        <h2 className="text-3xl font-bold">What's Included</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Authentication & Security</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>JWT access & refresh tokens with rotation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Google OAuth 2.0 (extensible to other providers)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Role-based access control (RBAC)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Password hashing with bcrypt</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Device tracking for tokens</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Developer Experience</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>TypeScript strict mode</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Swagger & Scalar API documentation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Global response wrapper</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Request validation with class-validator</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Hot reload and fast refresh</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Getting Started CTA */}
      <div className="bg-card border rounded-xl p-10 text-center space-y-6">
        <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Follow our quick start guide to have the template running in just a few minutes, 
          or dive deep into the documentation to understand every aspect of the template.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button asChild size="lg" className="text-base">
            <Link to="/nestjs/mongodb/quick-start">
              Get Started
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <a
              href="https://github.com/ALIRAZA47/template-nestjs-mongodb"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitBranch className="mr-2 h-5 w-5" />
              View on GitHub
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function QuickLinkCard({ 
  title, 
  description, 
  href 
}: { 
  title: string
  description: string
  href: string
}) {
  return (
    <Link 
      to={href}
      className="group block p-6 bg-card border rounded-xl hover:border-primary/50 transition-all hover:shadow-md"
    >
      <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
    </Link>
  )
}

